import React, { useEffect, useState } from "react";
import api, { formatApiError } from "../../lib/api";
import { Button } from "../ui/button";
import { Loader2, Sparkles, CheckCircle2, XCircle, MessageSquareWarning, PauseCircle, PlayCircle, Eye, Star, EyeOff, Trash2 } from "lucide-react";
import { toast } from "sonner";

const STATUS_STYLE = {
  draft: { label: "Bozza", cls: "bg-zinc-500/15 text-zinc-300 border-zinc-500/30" },
  in_review: { label: "In revisione", cls: "bg-amber-500/15 text-amber-400 border-amber-500/30" },
  published: { label: "Pubblicato", cls: "bg-[#00E676]/15 text-[#00E676] border-[#00E676]/30" },
  suspended: { label: "Sospeso", cls: "bg-red-500/15 text-red-400 border-red-500/30" },
  archived: { label: "Archiviato", cls: "bg-zinc-700/30 text-zinc-500 border-zinc-600/30" },
};
const FILTERS = [["in_review", "In revisione"], ["published", "Pubblicati"], ["suspended", "Sospesi"], ["", "Tutti"]];

function Stat({ label, value, color }) {
  return <div className="bg-[#1C1C1F] border border-white/10 rounded-xl p-4"><div className="text-2xl font-display font-bold" style={{ color }}>{value}</div><div className="text-xs text-zinc-500 uppercase tracking-wide">{label}</div></div>;
}

export default function ServicesModerationPanel() {
  const [view, setView] = useState("offerings");
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState(null);
  const [filter, setFilter] = useState("in_review");
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);

  const load = async () => {
    setLoading(true);
    const q = filter ? `?status=${filter}` : "";
    const [{ data: list }, { data: st }] = await Promise.all([
      api.get(`/admin/service-offerings${q}`), api.get("/admin/service-offerings/stats"),
    ]);
    setItems(list); setStats(st); setLoading(false);
  };
  useEffect(() => { load(); }, [filter]);

  const act = async (fn, msg) => { try { await fn(); toast.success(msg); load(); setDetail(null); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  const approve = (o) => act(() => api.post(`/admin/service-offerings/${o.id}/approve`), "Approvato e pubblicato");
  const reject = (o) => { const note = window.prompt("Motivo del rifiuto:"); if (!note) return; act(() => api.post(`/admin/service-offerings/${o.id}/reject`, { note }), "Rifiutato"); };
  const requestChanges = (o) => { const note = window.prompt("Correzioni richieste:"); if (!note) return; act(() => api.post(`/admin/service-offerings/${o.id}/request-changes`, { note }), "Correzioni richieste"); };
  const suspend = (o, v) => act(() => api.patch(`/admin/service-offerings/${o.id}/suspend`, { suspended: v }), v ? "Sospeso" : "Riattivato");

  return (
    <div className="space-y-5" data-testid="services-moderation-panel">
      <div className="flex items-center gap-3">
        <div className="h-10 w-10 rounded-xl bg-[#7DF9FF]/15 border border-[#7DF9FF]/30 flex items-center justify-center"><Sparkles className="h-5 w-5 text-[#7DF9FF]" /></div>
        <div><h2 className="font-display text-xl font-bold">Servizi Esperienze — Moderazione</h2><p className="text-sm text-zinc-400">Approva, rifiuta o sospendi i servizi dei fornitori.</p></div>
      </div>

      <div className="flex gap-2 bg-[#141417] border border-white/10 rounded-full p-1 w-fit">
        <button data-testid="mod-view-offerings" onClick={() => setView("offerings")} className={`text-sm rounded-full px-5 py-1.5 font-semibold ${view === "offerings" ? "bg-[#00E676] text-black" : "text-zinc-400"}`}>Servizi</button>
        <button data-testid="mod-view-reviews" onClick={() => setView("reviews")} className={`text-sm rounded-full px-5 py-1.5 font-semibold flex items-center gap-1 ${view === "reviews" ? "bg-[#00E676] text-black" : "text-zinc-400"}`}><Star className="h-3.5 w-3.5" /> Recensioni</button>
      </div>

      {view === "reviews" ? <ReviewsModeration /> : (<>
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Stat label="In revisione" value={stats.in_review} color="#F59E0B" />
          <Stat label="Pubblicati" value={stats.published} color="#00E676" />
          <Stat label="Prenotazioni" value={stats.bookings} color="#7DF9FF" />
          <Stat label="Ricavi" value={`€${(stats.revenue || 0).toFixed(0)}`} color="#FFD700" />
        </div>
      )}

      <div className="flex flex-wrap gap-2">
        {FILTERS.map(([k, l]) => (
          <button key={k || "all"} data-testid={`mod-filter-${k || "all"}`} onClick={() => setFilter(k)}
            className={`text-sm rounded-full px-4 py-1.5 border ${filter === k ? "bg-[#00E676] text-black border-[#00E676]" : "bg-white/5 text-zinc-300 border-white/15"}`}>{l}</button>
        ))}
      </div>

      {loading ? <div className="text-zinc-500 py-10 text-center"><Loader2 className="h-4 w-4 animate-spin inline" /> Caricamento…</div> : (
        <div className="space-y-3">
          {items.length === 0 && <div className="text-zinc-500 py-14 text-center border border-dashed border-white/10 rounded-xl">Nessun servizio in questo stato.</div>}
          {items.map((o) => {
            const st = STATUS_STYLE[o.status] || STATUS_STYLE.draft;
            const isOpen = detail === o.id;
            return (
              <div key={o.id} data-testid={`mod-card-${o.id}`} className="bg-[#1C1C1F] border border-white/10 rounded-xl p-4">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2"><span className="font-semibold">{o.title}</span><span className={`text-[10px] rounded-full px-2 py-0.5 border ${st.cls}`}>{st.label}</span></div>
                    <div className="text-xs text-zinc-500 mt-0.5">{o.provider_name} · {o.category} · {o.pricing_mode === "quote" ? "Preventivo" : `€${(o.price || 0).toFixed(2)}`} · {o.zone || "—"}</div>
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    <button data-testid={`mod-view-${o.id}`} onClick={() => setDetail(isOpen ? null : o.id)} className="text-xs flex items-center gap-1 rounded-md bg-white/5 border border-white/10 px-2.5 py-1.5"><Eye className="h-3 w-3" /> Dettagli</button>
                    {["in_review", "draft", "suspended"].includes(o.status) && <button data-testid={`mod-approve-${o.id}`} onClick={() => approve(o)} className="text-xs flex items-center gap-1 rounded-md bg-[#00E676]/15 text-[#00E676] border border-[#00E676]/30 px-2.5 py-1.5"><CheckCircle2 className="h-3 w-3" /> Approva</button>}
                    {o.status === "in_review" && <button data-testid={`mod-changes-${o.id}`} onClick={() => requestChanges(o)} className="text-xs flex items-center gap-1 rounded-md bg-amber-500/10 text-amber-400 border border-amber-500/20 px-2.5 py-1.5"><MessageSquareWarning className="h-3 w-3" /> Correzioni</button>}
                    {o.status === "in_review" && <button data-testid={`mod-reject-${o.id}`} onClick={() => reject(o)} className="text-xs flex items-center gap-1 rounded-md bg-red-500/10 text-red-400 border border-red-500/20 px-2.5 py-1.5"><XCircle className="h-3 w-3" /> Rifiuta</button>}
                    {o.status === "published" && <button data-testid={`mod-suspend-${o.id}`} onClick={() => suspend(o, true)} className="text-xs flex items-center gap-1 rounded-md bg-red-500/10 text-red-400 border border-red-500/20 px-2.5 py-1.5"><PauseCircle className="h-3 w-3" /> Sospendi</button>}
                    {o.status === "suspended" && <button data-testid={`mod-reactivate-${o.id}`} onClick={() => suspend(o, false)} className="text-xs flex items-center gap-1 rounded-md bg-[#00E676]/15 text-[#00E676] border border-[#00E676]/30 px-2.5 py-1.5"><PlayCircle className="h-3 w-3" /> Riattiva</button>}
                  </div>
                </div>
                {isOpen && (
                  <div className="mt-3 pt-3 border-t border-white/10 text-sm text-zinc-300 space-y-1.5" data-testid={`mod-detail-${o.id}`}>
                    {o.description && <p>{o.description}</p>}
                    <div className="grid grid-cols-2 gap-2 text-xs text-zinc-400">
                      <div>Flusso: {o.flow_mode}</div><div>Luogo: {o.location_mode}</div>
                      <div>Durata: {o.duration_min || "—"} min</div><div>Partecipanti: {o.min_participants}-{o.max_participants}</div>
                      <div>Occasioni: {(o.occasions || []).join(", ") || "—"}</div><div>Opt-in AI: {o.experience_optin ? "Sì" : "No"}</div>
                    </div>
                    {o.inclusions && <div className="text-xs"><b>Include:</b> {o.inclusions}</div>}
                    {o.cancellation_policy && <div className="text-xs"><b>Cancellazione:</b> {o.cancellation_policy}</div>}
                    {o.review_note && <div className="text-xs text-amber-400">Nota revisione: {o.review_note}</div>}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
      </>)}
    </div>
  );
}

function ReviewsModeration() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("published");
  const FILT = [["published", "Pubblicate"], ["hidden", "Nascoste"], ["removed", "Rimosse"], ["", "Tutte"]];
  const load = async () => { setLoading(true); const q = filter ? `?status=${filter}` : ""; const { data } = await api.get(`/admin/service-reviews${q}`); setItems(data); setLoading(false); };
  useEffect(() => { load(); }, [filter]);
  const moderate = async (r, action) => {
    const note = action === "publish" ? "" : (window.prompt("Nota di moderazione (facoltativa):") || "");
    try { await api.post(`/admin/service-reviews/${r.id}/moderate`, { action, note }); toast.success("Aggiornato"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <>
      <div className="flex flex-wrap gap-2">
        {FILT.map(([k, l]) => (
          <button key={k || "all"} data-testid={`rev-filter-${k || "all"}`} onClick={() => setFilter(k)}
            className={`text-sm rounded-full px-4 py-1.5 border ${filter === k ? "bg-[#00E676] text-black border-[#00E676]" : "bg-white/5 text-zinc-300 border-white/15"}`}>{l}</button>
        ))}
      </div>
      {loading ? <div className="text-zinc-500 py-10 text-center"><Loader2 className="h-4 w-4 animate-spin inline" /> Caricamento…</div> : (
        <div className="space-y-3">
          {items.length === 0 && <div className="text-zinc-500 py-14 text-center border border-dashed border-white/10 rounded-xl">Nessuna recensione.</div>}
          {items.map((r) => (
            <div key={r.id} data-testid={`rev-card-${r.id}`} className="bg-[#1C1C1F] border border-white/10 rounded-xl p-4">
              <div className="flex items-start justify-between gap-2 flex-wrap">
                <div className="min-w-0">
                  <div className="flex items-center gap-2"><span className="text-[#FFD700]">{"★".repeat(r.rating)}</span><span className="text-sm font-semibold">{r.customer_name}</span><span className="text-xs text-zinc-500">→ {r.provider_name}</span></div>
                  {r.comment && <p className="text-sm text-zinc-300 mt-1">{r.comment}</p>}
                  {r.provider_reply && <p className="text-xs text-zinc-400 mt-1 pl-2 border-l-2 border-[#7DF9FF]/40"><b>Risposta:</b> {r.provider_reply}</p>}
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {r.status !== "published" && <button data-testid={`rev-publish-${r.id}`} onClick={() => moderate(r, "publish")} className="text-xs flex items-center gap-1 rounded-md bg-[#00E676]/15 text-[#00E676] border border-[#00E676]/30 px-2.5 py-1.5"><CheckCircle2 className="h-3 w-3" /> Pubblica</button>}
                  {r.status === "published" && <button data-testid={`rev-hide-${r.id}`} onClick={() => moderate(r, "hide")} className="text-xs flex items-center gap-1 rounded-md bg-amber-500/10 text-amber-400 border border-amber-500/20 px-2.5 py-1.5"><EyeOff className="h-3 w-3" /> Nascondi</button>}
                  <button data-testid={`rev-remove-${r.id}`} onClick={() => moderate(r, "remove")} className="text-xs flex items-center gap-1 rounded-md bg-red-500/10 text-red-400 border border-red-500/20 px-2.5 py-1.5"><Trash2 className="h-3 w-3" /> Rimuovi</button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </>
  );
}
